import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { useChartTheme } from './useChartTheme';

/* Chart types that do NOT use cartesian axes */
const NON_CARTESIAN_TYPES = new Set([
  'pie', 'treemap', 'sunburst', 'sankey', 'gauge', 'radar', 'funnel',
]);

/** Return true when every series in the option is a non-cartesian type */
function isNonCartesian(option: EChartsOption): boolean {
  const series = Array.isArray(option.series)
    ? option.series
    : option.series
      ? [option.series]
      : [];
  return (
    series.length > 0 &&
    series.every((s) =>
      NON_CARTESIAN_TYPES.has((s as { type?: string }).type ?? ''),
    )
  );
}

type OptionPatches = Partial<EChartsOption> & { _seriesLabelColor?: string };

function mergeThemeIntoOption(
  option: EChartsOption,
  optionPatches: OptionPatches,
  animation: boolean
): EChartsOption {
  const nonCartesian = isNonCartesian(option);
  const seriesLabelColor = optionPatches._seriesLabelColor;
  const {
    grid: themeGrid,
    xAxis: themeXAxis,
    yAxis: themeYAxis,
    _seriesLabelColor: _ignored,
    ...themeUniversal
  } = optionPatches;

  const base: EChartsOption = {
    ...themeUniversal,
    ...option,
    animation,
  };

  if (themeUniversal.legend && base.legend && typeof base.legend === 'object' && !Array.isArray(base.legend)) {
    const existing = base.legend as Record<string, unknown>;
    const themeLegend = themeUniversal.legend as Record<string, unknown>;
    const textStyle = { ...(existing.textStyle as Record<string, unknown>), ...(themeLegend.textStyle as Record<string, unknown>) };
    base.legend = {
      ...existing,
      textStyle,
      pageTextStyle: existing.pageTextStyle ?? themeLegend.pageTextStyle,
    };
  }

  if (seriesLabelColor && Array.isArray(base.series)) {
    base.series = base.series.map((s) => {
      if (!s || typeof s !== 'object') return s;
      const series = { ...s } as Record<string, unknown>;
      const label = series.label;
      if (label && typeof label === 'object' && !Array.isArray(label)) {
        const labelObj = label as Record<string, unknown>;
        if (labelObj.show && labelObj.color === undefined) {
          series.label = { ...labelObj, color: seriesLabelColor };
        }
      }
      return series;
    });
  }

  if (!nonCartesian) {
    if (themeGrid) {
      base.grid = option.grid
        ? { ...themeGrid, ...option.grid }
        : { ...themeGrid };
    }
    if (themeXAxis) {
      base.xAxis = option.xAxis
        ? { ...themeXAxis, ...option.xAxis }
        : { ...themeXAxis };
    }
    if (themeYAxis) {
      base.yAxis = option.yAxis
        ? { ...themeYAxis, ...option.yAxis }
        : { ...themeYAxis };
    }
  }

  return base;
}

export interface EChartProps {
  option: EChartsOption;
  /** Pixel height (number) or CSS height string (e.g. '100%'). Default 260. */
  height?: number | string;
  onEvents?: Record<string, (params: unknown) => void>;
  loading?: boolean;
  animation?: boolean;
}

/**
 * Thin wrapper around echarts-for-react.
 * - Applies theme patches from useChartTheme.
 * - Skips grid / xAxis / yAxis patches for non-cartesian charts (pie, treemap, …)
 *   to avoid phantom axis lines on donuts.
 * - Uses notMerge + lazyUpdate=false so option changes apply immediately.
 */
export const EChart: React.FC<EChartProps> = ({
  option,
  height = 260,
  onEvents,
  loading = false,
  animation = false,
}) => {
  const { optionPatches } = useChartTheme();

  const mergedOption = useMemo(() => {
    return mergeThemeIntoOption(option, optionPatches as OptionPatches, animation);
  }, [optionPatches, option, animation]);

  const heightStyle = typeof height === 'number' ? `${height}px` : height;

  return (
    <ReactECharts
      option={mergedOption}
      style={{ height: heightStyle, width: '100%' }}
      notMerge
      lazyUpdate={false}
      showLoading={loading}
      onEvents={onEvents}
      opts={{ renderer: 'canvas' }}
    />
  );
};
